import { css } from '@emotion/react'
import { Alert, Cell, Grid, Heading, HFlow, InfoLabel, Link, Paper, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { useAcessoLotacaoOrEstagio } from 'components/auth/useAcessoLotacao'
import { ContextualHelpCard } from 'components/ContextualHelpCard'
import { DecimalField } from 'components/form'
import { usePecField } from 'components/form/core/hooks/useField'
import { add, isAfter, parseISO } from 'date-fns'
import { useAcompanhamentoPreNatalQuery, useMedicoesAtendimentosAnterioresQuery } from 'graphql/hooks.generated'
import { SexoEnum } from 'graphql/types.generated'
import { useServerTime } from 'hooks/useServerTime'
import preNatalPesoGestacionalImage from 'images/atendimento/acompanhamentos/pre-natal/pre-natal-peso-gestacional.webp'
import { useMemo } from 'react'
import { useField } from 'react-final-form'
import { useHistory, useRouteMatch } from 'react-router'
import Permissions from 'types/Permissions'
import { calcularIMC, estadoNutricional } from 'util/atendimento'
import { calculateFractionalAgeInYears } from 'util/date/calculateAge'
import { formatDate, toDate } from 'util/date/formatDate'
import { MetaPath } from 'util/metaPath'
import { formatNumber, parseNumber } from 'util/number'
import { isValid } from 'util/validation/Util'
import { meta as soapMeta } from 'view/atendimentos/atendimento-individual/model'
import {
  convertMedicaoAnterior,
  convertMedicaoAtendimentoAtual,
  convertMedicaoOutrosAtendimentos,
  convertMedicoesNoNascimento,
} from 'view/atendimentos/detail/components/modals/medicoes/converter-medicoes'
import { categoriaImcReferenciaRecord } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/ganho-peso/model-graficoGanhoPeso'
import { getCategoriaImcReferencia } from 'view/atendimentos/detail/components/modals/medicoes/graficos/pre-natal/ganho-peso/util-graficoGanhoPeso'
import { MedicaoAntropometricaPreNatal } from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/model-medicoesPreNatal'
import {
  getMedicaoForImcReferencia,
  getMedicoesAntropometricasPreNatal,
} from 'view/atendimentos/detail/components/modals/medicoes/pre-natal/util-medicoesPreNatal'
import { PeriodoGestacaoModel } from 'view/atendimentos/detail/components/modals/types/PeriodoGestacaoModel'
import { usePeriodoGestacional } from 'view/atendimentos/detail/soap/aside/pre-natal/hooks/usePeriodoGestacional'
import { getDumAtual } from 'view/atendimentos/detail/soap/aside/pre-natal/util/util-acompanhamentoPreNatal'
import { getSoapFieldLabelGroup } from 'view/atendimentos/detail/soap/fields-summary-modal/getSoapFieldLabelGroup'
import { grupoCboAcompanhamentoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/acessos'
import { TipoPreNatal } from 'view/atendimentos/detail/soap/pre-natal/model-preNatal'
import { CidadaoAtendimento } from 'view/atendimentos/types/CidadaoAtendimento'
import { HistoricoMedicaoModel } from 'view/atendimentos/types/model-historicoMedicao'

import { useIsCriancaPreTermo } from '../hooks/useIsCriancaPreTermo'
import { AntropometriaAccordionModel } from './AntropometriaAccordion'

interface ImcPanelProps {
  name: MetaPath<AntropometriaAccordionModel>
  cidadao: CidadaoAtendimento
  gestacoes: ReadonlyArray<PeriodoGestacaoModel>
  isGestante?: boolean
  dataAtendimento: Date
  isEscutaInicial?: boolean
  prontuarioId?: ID
  tipoPreNatal?: TipoPreNatal
}

export function ImcPanel(props: Readonly<ImcPanelProps>) {
  const { name, cidadao, gestacoes, isGestante, dataAtendimento, isEscutaInicial, prontuarioId, tipoPreNatal } = props

  const {
    input: { value },
    meta: { error },
  } = useField<AntropometriaAccordionModel>(name.absolutePath(), {
    subscription: { value: true, error: true },
  })

  const history = useHistory()
  const match = useRouteMatch()
  const { getServerTimeNow } = useServerTime()
  const hoje = getServerTimeNow()

  const theme = useTheme()
  const styles = createStyles(theme)

  const { hasAuthorization, hasCboAuth } = useAcessoLotacaoOrEstagio()
  const canAccessPreNatal = hasCboAuth(grupoCboAcompanhamentoPreNatal)
  const canAccessProntuario = hasAuthorization(Permissions.visualizarProntuario)

  const modalUrl = `${match.url}/historicoMedicoes`
  const handleClickGanhoPeso = () => {
    history.push(modalUrl)
  }

  const imc = useMemo(
    () =>
      isValid(error?.peso) && isValid(error?.altura) && calcularIMC(parseNumber(value.peso), parseNumber(value.altura)),
    [error, value.peso, value.altura]
  )

  const antropometriaAtendimentoAtual = useMemo<AntropometriaAccordionModel | undefined>(
    () =>
      isValid(error?.peso) &&
      isValid(error?.altura) && {
        peso: value.peso,
        altura: value.altura,
      },
    [error, value.peso, value.altura]
  )

  const idadeDuranteMedicao = calculateFractionalAgeInYears(cidadao.dataNascimento, dataAtendimento.getTime())

  const isSexoBinario = cidadao.sexo === SexoEnum.MASCULINO || cidadao.sexo === SexoEnum.FEMININO

  const {
    input: { value: idadeGestacionalEmSemanas },
  } = usePecField<number | undefined>({
    name: soapMeta?.objetivo?.atendimentosEspecificos?.puericultura?.prenatalPartoNascimento?.idadeGestacional?.semanas,
    subscription: { value: true },
  })

  const { isPreTermoNaFaixa64Semanas } = useIsCriancaPreTermo({
    prontuarioId: prontuarioId!,
    dataNascimento: cidadao.dataNascimento,
    isSexoBinario,
    idadeGestacional: idadeGestacionalEmSemanas,
  })

  const calcularImc = imc && !isGestante && !isPreTermoNaFaixa64Semanas

  const msgEstadoNutricional = useMemo(
    () =>
      calcularImc &&
      estadoNutricional(imc, cidadao.sexo, idadeDuranteMedicao, gestacoes, cidadao.identidadeGeneroDbEnum),
    [calcularImc, imc, cidadao.sexo, cidadao.identidadeGeneroDbEnum, idadeDuranteMedicao, gestacoes]
  )

  const {
    fields: { altura, peso },
  } = getSoapFieldLabelGroup(soapMeta.objetivo.antropometria)

  const {
    input: { value: dumAtendimentoAtual },
  } = usePecField({ name: soapMeta.objetivo.dum, subscription: { value: true } })

  const {
    input: { value: medicoesAnterioresAtendimentoAtual },
  } = usePecField({ name: soapMeta.medicoesAnteriores, subscription: { value: true } })

  const skipAcompanhamentoPreNatalQuery = !prontuarioId || !canAccessPreNatal
  const {
    data: { atendimentosProfUltimaGestacao },
    loading: loadingAcompanhamentoPreNatal,
  } = useAcompanhamentoPreNatalQuery({
    variables: { input: prontuarioId },
    skip: skipAcompanhamentoPreNatalQuery,
  })

  const dumPersistida = useMemo(() => getDumAtual(atendimentosProfUltimaGestacao), [atendimentosProfUltimaGestacao])
  const dumAtendimentoAtualParsed = dumAtendimentoAtual ? parseISO(dumAtendimentoAtual) : undefined

  const { dataInicioGestacao } = usePeriodoGestacional({
    prontuarioId,
    dum: dumPersistida,
    dumAtendimentoAtual: dumAtendimentoAtualParsed,
    dataAtendimento,
    skip: loadingAcompanhamentoPreNatal,
  })

  const skipAtendimentosAnterioresQuery = !prontuarioId || !canAccessProntuario
  const { data: medicoesAnteriores } = useMedicoesAtendimentosAnterioresQuery({
    variables: { prontuarioId },
    skip: skipAtendimentosAnterioresQuery,
  })

  const historicoMedicoes: HistoricoMedicaoModel[] = useMemo(
    () => [
      ...[...(medicoesAnteriores?.medicoes ?? [])]
        .sort((a, b) => b.dataMedicao - a.dataMedicao)
        .map(convertMedicaoOutrosAtendimentos),

      ...(antropometriaAtendimentoAtual
        ? [convertMedicaoAtendimentoAtual(null, antropometriaAtendimentoAtual, dataAtendimento.getTime())]
        : []),

      ...[...(medicoesAnterioresAtendimentoAtual ?? [])]
        .sort((a, b) => Number(b.data) - Number(a.data))
        .map(convertMedicaoAnterior),

      ...(medicoesAnteriores?.medicoesNoNascimento && cidadao.dataNascimento != null
        ? [convertMedicoesNoNascimento(medicoesAnteriores.medicoesNoNascimento, cidadao.dataNascimento)]
        : []),
    ],
    [
      medicoesAnteriores,
      medicoesAnterioresAtendimentoAtual,
      dataAtendimento,
      cidadao.dataNascimento,
      antropometriaAtendimentoAtual,
    ]
  )

  const converterParaMedicoesAntropometricasPreNatal = useMemo((): MedicaoAntropometricaPreNatal[] => {
    if (!Array.isArray(medicoesAnterioresAtendimentoAtual)) return []

    return medicoesAnterioresAtendimentoAtual.map((item) => ({
      dataMedicao: toDate(item.data).getTime(),
      peso: Number(item.peso),
      altura: Number(item.altura),
      origem: 'outroAtendimento',
    }))
  }, [medicoesAnterioresAtendimentoAtual])

  const medicoesAntropometricasPreNatal = useMemo(
    () => getMedicoesAntropometricasPreNatal(historicoMedicoes, tipoPreNatal),
    [historicoMedicoes, tipoPreNatal]
  )

  const medicaoForImcReferencia = useMemo(() => {
    const dataValida = dataInicioGestacao instanceof Date && !Number.isNaN(dataInicioGestacao.getTime())
    const medicoesValidas = Array.isArray(medicoesAntropometricasPreNatal) && !medicoesAntropometricasPreNatal.isEmpty()

    if (!dataValida || !medicoesValidas) return undefined

    const todasMedicoes = [...medicoesAntropometricasPreNatal, ...converterParaMedicoesAntropometricasPreNatal]
    const medicoesUnicasPorData = Array.from(
      new Map(todasMedicoes.toReversed().map((medicao) => [medicao.dataMedicao, medicao])).values()
    ).toReversed()

    return getMedicaoForImcReferencia(dataInicioGestacao, medicoesUnicasPorData)
  }, [dataInicioGestacao, medicoesAntropometricasPreNatal, converterParaMedicoesAntropometricasPreNatal])

  const imcReferencia = useMemo(() => {
    if (!medicaoForImcReferencia) return undefined

    const { peso, altura, dataMedicao } = medicaoForImcReferencia
    if (peso != null && altura != null && dataMedicao != null) {
      const imcReferencia = calcularIMC(peso, altura)
      const categoriaReferencia = getCategoriaImcReferencia(Number(imcReferencia))
      return {
        imc: imcReferencia,
        dataMedicao: new Date(dataMedicao).toISOString(),
        categoriaReferencia: categoriaReferencia,
      }
    }

    return undefined
  }, [medicaoForImcReferencia])

  const categoriaImcReferencia = getCategoriaImcReferencia(Number(imcReferencia?.imc))
  const alertType = categoriaImcReferenciaRecord[categoriaImcReferencia].alertType

  const dumAvaliada = dumAtendimentoAtual || dumPersistida

  const prazoPesoPreGestacional = dumAvaliada && isAfter(hoje, add(new Date(dumAvaliada), { weeks: 8 }))

  return (
    <Paper style={{ padding: '0.625rem' }}>
      <Grid gap={1} alignItems='flex-end' justifyContent='flex-start'>
        <Cell xs={12} lg={8} flexGrow={1}>
          <VFlow>
            <HFlow>
              <DecimalField name={name.peso} label={peso.label} maxLength={7} precisao={3} />
              <DecimalField name={name.altura} label={altura.label} maxLength={5} precisao={1} />
              <VFlow>
                {isGestante ? (
                  <Paper style={styles.paper} data-testid='Paper.ImcPreGestacional'>
                    <VFlow vSpacing={0.25}>
                      <HFlow hSpacing={0.5}>
                        <Heading level={5} style={{ whiteSpace: 'nowrap' }}>
                          IMC pré-gestacional:
                        </Heading>
                        {!!imcReferencia?.imc && <Text component='span'>{imcReferencia.imc} kg/m²</Text>}
                      </HFlow>
                      {imcReferencia?.categoriaReferencia && (
                        <Alert
                          inline
                          type={alertType}
                          styles={{
                            wrapper: [styles.alertIcon, styles.alertImcWrapper],
                          }}
                        >
                          {imcReferencia.categoriaReferencia}
                        </Alert>
                      )}
                      <Text component='span' fontStyle='italic'>
                        {imcReferencia?.dataMedicao
                          ? `Registrado em: ${formatDate(imcReferencia?.dataMedicao)}`
                          : 'Não registrado'}
                      </Text>
                    </VFlow>
                  </Paper>
                ) : (
                  <InfoLabel title='IMC'>
                    <Text component='span' fontSize={1.5} fontWeight='bold' fontStyle='normal' data-testid='Text.IMC'>
                      {calcularImc ? formatNumber(imc, 2) : '-'}
                    </Text>
                  </InfoLabel>
                )}
              </VFlow>
            </HFlow>
            {prazoPesoPreGestacional && isGestante && !imcReferencia && (
              <Alert
                type='danger'
                styles={{
                  wrapper: styles.imcPreGestacionalAlertIcon,
                }}
              >
                <Text component='p' fontWeight='normal' color='danger'>
                  <Text component='span' fontWeight='bold' color='danger'>
                    Cálculo do IMC pré-gestacional fora do período de referência.{' '}
                  </Text>
                  O IMC pré-gestacional requer um registro de peso e altura até dois meses antes da gestação ou até a 8ª
                  semana de gestação.
                </Text>
              </Alert>
            )}
            {isGestante && (
              <ContextualHelpCard
                title='Monitoramento de peso na gestação'
                dataTestId='ContextualHelpCard.MonitoramentoPesoGestacao'
                description={
                  <Text>
                    Na gestação, o diagnóstico nutricional é baseado no IMC pré-gestacional e no{' '}
                    {isEscutaInicial ? (
                      'Gráfico de Ganho de Peso.'
                    ) : (
                      <Link onClick={handleClickGanhoPeso} style={{ fontWeight: 'normal' }}>
                        Gráfico de Ganho de Peso.
                      </Link>
                    )}
                    <br />
                    O cálculo do IMC pré-gestacional requer um registro de peso e altura feito até 2 meses antes da
                    gestação ou até a 8ª semana de gestação.
                    <br />
                    Novos cálculos de IMC não são realizados no período gestacional.
                  </Text>
                }
                image={preNatalPesoGestacionalImage}
                imageHeight={80}
                maxLines={isEscutaInicial ? 1 : 2}
              />
            )}
            {isPreTermoNaFaixa64Semanas && (
              <Alert type='info' data-testid='Alert.ImcPreTermo' styles={{ wrapper: styles.alertImcPreTermo }}>
                <Text component='p' fontWeight='normal' color='info'>
                  <Text component='span' fontWeight='bold' color='info'>
                    O IMC não se aplica para crianças prematuras.{' '}
                  </Text>
                  Acompanhe o crescimento utilizando os{' '}
                  {isEscutaInicial ? (
                    'Gráficos para Pré-termo.'
                  ) : (
                    <Link onClick={handleClickGanhoPeso} style={{ fontWeight: 'normal' }}>
                      Gráficos para Pré-termo.
                    </Link>
                  )}
                </Text>
              </Alert>
            )}
          </VFlow>
        </Cell>
        {msgEstadoNutricional && (
          <Cell xs={12} lg={4}>
            <Alert inline type={msgEstadoNutricional.alertType}>
              {msgEstadoNutricional.message}
            </Alert>
          </Cell>
        )}
      </Grid>
    </Paper>
  )
}

const createStyles = (theme: Theme) => ({
  imcPreGestacionalAlertIcon: css`
    svg {
      width: 1rem;
      height: 1rem;
    }
  `,

  paper: css`
    border: 0;
    background: ${theme.pallete.surface.background};
    padding: 0.5rem;
    ${theme.breakpoints.up('lg')} {
      max-height: 5.75rem;
    }
    ${theme.breakpoints.down('lg')} {
      max-height: 6.5rem;
    }
  `,

  alertIcon: css`
    svg {
      width: 1rem;
      height: 1rem;
    }
  `,
  alertImcWrapper: css`
    background-color: transparent;
    border-width: 0;
    padding: 0;
    min-height: 0;
  `,
  alertImcPreTermo: css`
    padding: 0.5rem 1rem;
  `,
})
